import React, { useEffect } from "react";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

import BackBar from "../../../../common/nav/BackBar";
import SaveButton from "../../../../common/nav/SaveButton";
import Form from "../../../../common/forms/Form";
import FormInput from "../../../../common/forms/FormInput";
import NotificationModal from "../../../../common/settings-modal/NotificationModal";

import SelenaA from "../../../../assets/img/service/selena-a.webp";

import { ValType } from "../../../../utils/ValidationFunctions";
import { RequestState } from "../../../../utils/RequestState";

import styles from "../EditBase.module.scss";
import LoadingSpinner from "../../../../common/LoadingSpinner";

type Props = {
  full_name?: string;
  gender?: string;
  birthday?: number;
  prefecture?: string;
  address_line_1?: string;
  address_line_2?: string;
  // FormInputにnumberはないのでphoneもstring
  phone?: string;
  handleClose: () => void;
  handleInputChange: (value: string | undefined, name: string) => void;
  handleSubmit: () => void;
  requestState?: RequestState;
};

const EditContact: React.FC<Props> = ({
  full_name,
  gender,
  birthday,
  prefecture,
  address_line_1,
  address_line_2,
  phone,
  handleClose,
  handleInputChange,
  handleSubmit,
  requestState,
}) => {
  const genNotificationColor = () => {
    switch (requestState) {
      case RequestState.Pending:
      case RequestState.Failed:
        return "#fe493d";
      case RequestState.Doing:
        return "#f79b00";
      case RequestState.Success:
        return "#27c900";
      default:
        return "";
    }
  };

  const genNotificationMessage = () => {
    switch (requestState) {
      case RequestState.Pending:
        return "保存していない変更があります";
      case RequestState.Doing:
        return "通信中です";
      case RequestState.Success:
        return "保存が完了しました";
      case RequestState.Failed:
        return "保存に失敗しました";
      default:
        return "";
    }
  };

  const genNotificationIcon = (): IconProp | undefined => {
    switch (requestState) {
      case RequestState.Pending:
      case RequestState.Failed:
        return ["fal", "exclamation-circle"];
      case RequestState.Success:
        return ["fal", "check-circle"];
      default:
        return undefined;
    }
  };

  useEffect(() => {
    if (requestState === RequestState.Doing) {
      window.scrollTo(0, 0);
    }
  }, [requestState]);

  return (
    <div className={styles["p-edit_wrapper"]}>
      {requestState !== RequestState.Doing ? (
        <>
          <BackBar
            title={"ご連絡先"}
            editable={true}
            handleClose={handleClose}
            saveHandler={handleSubmit}
            hideSaveButton={
              requestState !== RequestState.Pending ? true : false
            }
          />
          <div className={`${styles["p-edit__inner"]} ${styles["x-padding"]}`}>
            <div className={styles["p-edit__title"]}>
              ご連絡先
              <div className={styles["p-edit__title-desc"]}>
                ご連絡先の情報は、SELeCTからのご連絡に使用いたします。
                <br />
                データベース上には公開されません。
              </div>
            </div>

            <Form handler={() => {}}>
              <div className={styles["p-edit__form"]}>
                <FormInput
                  type="text"
                  label="氏名"
                  label_class={styles["p-edit__form-title"]}
                  style_class={styles["p-edit__form-input"]}
                  id="full_name"
                  name="sonic_full_name"
                  placeholder="山田 瀬麗奈"
                  initial_value={full_name}
                  handler={handleInputChange}
                />
              </div>
              <div className={styles["p-edit__form"]}>
                <FormInput
                  type="select"
                  options={[
                    ["", "選択してください"],
                    ["male", "男性"],
                    ["female", "女性"],
                    ["nonbinary", "その他"],
                  ]}
                  label="性別"
                  label_class={styles["p-edit__form-title"]}
                  id="gender"
                  name="sonic_gender"
                  initial_value={gender}
                  select_class={styles["p-edit__form-input"]}
                  holder_class={styles["p-edit__form-input-novalue"]}
                  handler={handleInputChange}
                />
              </div>
              <div className={styles["p-edit__form"]}>
                <FormInput
                  type="date"
                  label="生年月日"
                  label_class={styles["p-edit__form-title"]}
                  style_class={styles["p-edit__form-input"]}
                  input_wrap_class={styles["date-wrap"]}
                  id="birthday"
                  name="sonic_birthday"
                  placeholder="2000/05/03"
                  initial_value={birthday}
                  handler={handleInputChange}
                />
              </div>

              <div className={styles["p-edit__form"]}>
                <FormInput
                  type="prefecture"
                  placeholder="選択してください"
                  label="お住まいの都道府県"
                  label_class={styles["p-edit__form-title"]}
                  id="state"
                  name="sonic_prefecture"
                  initial_value={prefecture}
                  select_class={styles["p-edit__form-input"]}
                  holder_class={styles["p-edit__form-input-novalue"]}
                  handler={handleInputChange}
                />
              </div>
              <div className={styles["p-edit__form"]}>
                <FormInput
                  type="text"
                  label="住所１（番地まで）"
                  label_class={styles["p-edit__form-title"]}
                  id="address"
                  name="sonic_address_line_1"
                  placeholder="〇〇市〇〇区１ー２−３"
                  initial_value={address_line_1}
                  style_class={styles["p-edit__form-input"]}
                  handler={handleInputChange}
                />
              </div>
              <div className={styles["p-edit__form"]}>
                <FormInput
                  type="text"
                  label="住所２（建物名）"
                  label_class={styles["p-edit__form-title"]}
                  id="building"
                  name="sonic_address_line_2"
                  placeholder="〇〇マンション101号室"
                  initial_value={address_line_2}
                  style_class={styles["p-edit__form-input"]}
                  handler={handleInputChange}
                />
              </div>
              <div
                className={`${styles["p-edit__form"]} ${styles["p-edit__form--last"]}`}
              >
                <FormInput
                  type="text"
                  label="お電話番号"
                  label_class={styles["p-edit__form-title"]}
                  id="phone"
                  name="sonic_phone"
                  placeholder="01234567890"
                  initial_value={phone}
                  style_class={styles["p-edit__form-input"]}
                  handler={handleInputChange}
                  val_type={ValType.Phone}
                />
              </div>
            </Form>
            {requestState === RequestState.Pending ? (
              <SaveButton onClick={handleSubmit} />
            ) : null}
          </div>
        </>
      ) : (
        <LoadingSpinner space={1} />
      )}
      {requestState !== RequestState.Waiting ? (
        <NotificationModal
          color={genNotificationColor()}
          icon_class={genNotificationIcon()}
          message={genNotificationMessage()}
          img_src={requestState === RequestState.Success ? SelenaA : undefined}
        />
      ) : null}
    </div>
  );
};

export default EditContact;
